/**
 * Render the first page of a PDF to a WebP blob using pdfjs-dist.
 * The pdfjs library is dynamically imported to keep it out of the main bundle.
 * The worker is a Vite-bundled, same-origin asset (not a runtime CDN fetch), so
 * it works offline and avoids cross-origin module-worker fragility on mobile.
 */
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

export const renderPdfFirstPageToWebp = async (
  file: File | Blob,
  options: {
    scale?: number;
    quality?: number;
    onLog?: (msg: string) => void;
  } = {},
): Promise<Blob> => {
  const { scale = 2.0, quality = 1.0, onLog } = options;
  const log = (msg: string) => onLog?.(msg);

  const pdfjsLib = await import("pdfjs-dist");
  log("pdfjs-dist loaded");

  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
  log(`Worker src set to: ${workerUrl}`);

  log("Reading array buffer...");
  const arrayBuffer = await file.arrayBuffer();
  log("Array buffer read. getDocument...");

  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  log("PDF Document loaded. getPage(1)...");

  const page = await pdf.getPage(1);
  log("Page 1 loaded. Rendering...");

  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Could not create canvas context");

  canvas.height = viewport.height;
  canvas.width = viewport.width;

  await page.render({
    canvasContext: context,
    viewport,
    canvas,
  }).promise;
  log("Page rendered to canvas.");

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        log("Canvas toBlob completed.");
        if (!blob) {
          reject(new Error("Failed to create WebP blob"));
          return;
        }
        resolve(blob);
      },
      "image/webp",
      quality,
    );
  });
};
